<template>
  <div class="tracking-app tracking-app-zh">
    <header class="tracking-nav">
      <div class="tracking-brand"><b>Phoenix</b></div>
      <nav>
        <router-link to="/">海运 / 空运</router-link>
        <router-link class="active" to="/express">空运（快递）</router-link>
        <router-link to="/quota">申请报价</router-link>
      </nav>
    </header>

    <section class="tracking-hero">
      <div class="hero-content">
        <small>PHOENIX EXPRESS</small>
        <h1>空运（快递）货物追踪</h1>
        <p>输入系统单号或物流商单号，实时查询最新物流流转与派送轨迹。</p>
        <div class="tracking-search">
          <input v-model.trim="no" maxlength="50" @keyup.enter="search" placeholder="输入系统单号或物流商单号 (如 EXPR260828001 / AIRE260700453)">
          <button :disabled="loading" @click="search">{{ loading ? '查询中...' : '查询运单' }}</button>
        </div>
        <div v-if="error" class="query-error">{{ error }}</div>
      </div>
    </section>

    <main v-if="shipment" class="tracking-main">
      <section class="shipment-card">
        <div class="shipment-head">
          <div class="shipment-title">
            <div class="transport-icon">✈</div>
            <div>
              <h2>{{ shipment.systemNo || shipment.system_no }}</h2>
              <p class="carrier-ref">
                物流商：<b>{{ carrierName(shipment.carrier) }}</b> | 物流商单号：<b>{{ shipment.carrierNo || shipment.carrier_no }}</b>
                <span v-if="shipment.origin || shipment.destination"> ({{ shipment.origin || '始发站' }} → {{ shipment.destination || '目的站' }})</span>
              </p>
            </div>
          </div>
          <span class="mode-pill status-active">{{ shipment.currentStatus || shipment.current_status || '运输中' }}</span>
        </div>

        <!-- 阶段里程碑流转 -->
        <div class="milestones-section">
          <h3>核心物流节点</h3>
          <div class="milestones-stepper">
            <div
              v-for="(e, idx) in (shipment.events || [])"
              :key="e.nodeCode"
              class="stepper-item"
              :class="{
                completed: e.statusCode === 'COMPLETED',
                processing: e.statusCode === 'PROCESSING',
                pending: e.statusCode === 'PENDING'
              }"
            >
              <div class="stepper-circle">
                <span v-if="e.statusCode === 'COMPLETED'">✓</span>
                <span v-else>{{ idx + 1 }}</span>
              </div>
              <div class="stepper-text">
                <b>{{ e.nodeName }}</b>
                <small v-if="e.occurredAt">{{ e.occurredAt }}</small>
                <small v-else class="text-muted">{{ statusText(e.statusCode) }}</small>
              </div>
              <div v-if="idx < (shipment.events || []).length - 1" class="stepper-line"></div>
            </div>
          </div>
        </div>

        <!-- 官网实时抓取轨迹明细流 -->
        <div class="timeline-section">
          <div class="section-head">
            <h3>实时轨迹明细</h3>
            <small v-if="crawlerEvents.length">共获取 {{ crawlerEvents.length }} 条实时物流事件（最新排在最前）</small>
          </div>

          <div v-if="crawlerEvents.length" class="timeline">
            <article
              v-for="(ce, idx) in crawlerEvents"
              :key="idx"
              class="timeline-event"
              :class="{ 'latest-event': idx === 0 }"
            >
              <div class="timeline-dot" :class="{ done: idx === 0 }"></div>
              <div class="timeline-content">
                <div class="event-title">
                  <b>{{ ce.status || ce.description || '状态更新' }}</b>
                  <span v-if="ce.location" class="location-badge">{{ ce.location }}</span>
                  <span v-if="idx === 0" class="tag-latest">最新进展</span>
                </div>
                <small class="event-time">{{ ce.time }}</small>
                <p v-if="ce.description && ce.description !== ce.status" class="event-desc">{{ ce.description }}</p>
              </div>
            </article>
          </div>

          <!-- 无爬虫明细时降级显示配置的事件 -->
          <div v-else class="timeline">
            <article
              v-for="e in (shipment.events || [])"
              :key="e.nodeCode"
              class="timeline-event"
            >
              <div class="timeline-dot" :class="{ done: e.statusCode === 'COMPLETED', active: e.statusCode === 'PROCESSING' }"></div>
              <div class="timeline-content">
                <div class="event-title">
                  <b>{{ e.nodeName }}</b>
                  <span class="status-tag" :class="e.statusCode ? e.statusCode.toLowerCase() : ''">{{ statusText(e.statusCode) }}</span>
                </div>
                <small v-if="e.occurredAt" class="event-time">{{ e.occurredAt }}</small>
                <p v-if="e.remark" class="event-desc">{{ e.remark }}</p>
              </div>
            </article>
          </div>
        </div>
      </section>
    </main>

    <section v-else class="query-placeholder">
      <div class="placeholder-icon">📦</div>
      <p>输入快递系统单号或物流商运单号，查看全流程物流流转与清关派送进度</p>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ExpressTracking',
  data: () => ({
    no: '',
    shipment: null,
    error: '',
    loading: false
  }),
  computed: {
    crawlerEvents () {
      if (!this.shipment || !this.shipment.crawlerEvents || !this.shipment.crawlerEvents.length) {
        return []
      }
      // Return events sorted latest-first (DESC)
      return [...this.shipment.crawlerEvents].reverse()
    }
  },
  methods: {
    sanitizeText (val) {
      if (!val || typeof val !== 'string') return val || ''
      let s = val
      s = s.replace(/\b(cargo has been|shipment has been|goods have been)\s+(picked up|collected|received|delivered|handed over|released)\s+(by|to)\s+[^|\n\r;]+/gi, 'Cargo has been $2.')
      s = s.replace(/\b(picked up|collected|received|delivered|handed over|released)\s+(by|to)\s+[^|\n\r;]+/gi, '$1.')
      s = s.replace(/\b(signed\s+for\s+by|signed\s+by|signed\s+for)\s+[^|\n\r;]+/gi, 'Signed.')
      s = s.replace(/\bout for delivery with\s+[^|\n\r;]+/gi, 'Out for delivery.')
      s = s.replace(/\b(transferred|handed)\s+to\s+[^|\n\r;]+/gi, '$1.')
      s = s.replace(/\b(consignee|agent|broker|forwarder|trucker|haulier|signatory|driver|customs broker)[:：]\s*[^|\n\r;]+/gi, '$1: ***')
      s = s.replace(/(?:货物|快件)?已被[^|，,。\n\r;]+?(提取|提走|签收|代收|领走|放行|交接|派送)/g, '货物已被$1')
      s = s.replace(/已由[^|，,。\n\r;]+?(代派|提货|签收|提取|承运|派送|交接|代收|提走)/g, '已$1')
      s = s.replace(/(?:由|经)[^|，,。\n\r;]+?(代派|提货|签收|提取|承运|派送|交接|代收|提走)/g, '已$1')
      s = s.replace(/(提货人|提货单位|提货公司|收货人|收货单位|收货公司|提货代理|收货代理|签收人|交接人|经办人|派送人|承运代理|报关行)[:：]\s*[^|，,。\n\r;]+/g, '$1：***')
      s = s.replace(/\.{2,}/g, '.')
      return s.trim()
    },
    async search () {
      this.error = ''
      this.shipment = null
      const term = (this.no || '').trim()
      if (!term) {
        this.error = '请输入系统单号或物流商单号'
        return
      }
      this.loading = true
      try {
        const response = await axios.get('/api/express/' + encodeURIComponent(term))
        const data = response.data || {}
        data.currentStatus = this.sanitizeText(data.currentStatus || data.current_status)
        data.events = (data.events || []).map(ev => ({
          ...ev,
          remark: this.sanitizeText(ev.remark)
        }))
        data.crawlerEvents = (data.crawlerEvents || []).map(cev => ({
          ...cev,
          status: this.sanitizeText(cev.status),
          description: this.sanitizeText(cev.description)
        }))
        this.shipment = data
      } catch (e) {
        this.error = e.response?.status === 404 ? '未找到该快递运单，请核对单号后重试。' : '查询服务暂时不可用，请稍后重试。'
      } finally {
        this.loading = false
      }
    },
    carrierName (c) {
      return {
        CHINA_SOUTHERN: '南方航空 (CSAir)',
        DHL: 'DHL 国际快递',
        QIANQIU: '钱球 (BaslExpress)',
        COSCO: '中远海运',
        MSC: '地中海航运',
        MSK: '马士基',
        OOCL: '东方海外',
        TSL: '德翔海运',
        YML: '阳明海运',
        ANL: '澳航海运 (ANL)'
      }[c] || c
    },
    statusText (s) {
      return { PENDING: '待更新', PROCESSING: '进行中', COMPLETED: '已完成', FETCHED: '已抓取' }[s] || s || '待更新'
    }
  }
}
</script>

<style scoped>
.carrier-ref { margin: 6px 0 0; color: #607089; font-size: 13px; }
.carrier-ref b { color: #182b45; }
.status-active { background: #e6f7ec; color: #1f9d55; font-weight: 600; border: 1px solid #c2ebd0; }
.milestones-section { padding: 22px 28px; border-bottom: 1px solid #edf1f5; background: #fafcff; }
.milestones-section h3, .timeline-section h3 { margin: 0 0 16px; font-size: 15px; color: #182b45; font-weight: 700; }
.milestones-stepper { display: flex; align-items: flex-start; justify-content: space-between; position: relative; }
.stepper-item { display: flex; flex-direction: column; align-items: center; text-align: center; flex: 1; position: relative; }
.stepper-circle { width: 32px; height: 32px; border-radius: 50%; display: grid; place-items: center; font-size: 13px; font-weight: bold; background: #eaeff5; color: #7f90a4; z-index: 2; border: 2px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.06); }
.stepper-item.completed .stepper-circle { background: #1264d8; color: #fff; }
.stepper-item.processing .stepper-circle { background: #e8f0fe; color: #1264d8; border-color: #1264d8; }
.stepper-text { margin-top: 8px; display: flex; flex-direction: column; gap: 2px; }
.stepper-text b { font-size: 13px; color: #182b45; }
.stepper-text small { font-size: 11px; color: #7f90a4; }
.stepper-line { position: absolute; top: 16px; left: 50%; width: 100%; height: 2px; background: #e2e8f0; z-index: 1; }
.stepper-item.completed .stepper-line { background: #1264d8; }
.timeline-section { padding: 24px 28px; }
.section-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.section-head small { color: #8291a3; font-size: 12px; }
.timeline { display: flex; flex-direction: column; }
.timeline-event { display: grid; grid-template-columns: 24px 1fr; gap: 14px; position: relative; padding-bottom: 24px; }
.timeline-event:before { content: ""; position: absolute; left: 5px; top: 12px; bottom: 0; width: 2px; background: #e2e8f0; }
.timeline-event:last-child:before { display: none; }
.timeline-dot { width: 12px; height: 12px; border-radius: 50%; border: 2px solid #b9c5d2; background: #fff; z-index: 1; margin-top: 4px; }
.timeline-dot.done { border-color: #1264d8; background: #1264d8; }
.timeline-dot.active { border-color: #10b981; background: #10b981; }
.latest-event .timeline-dot { width: 14px; height: 14px; border-color: #10b981; background: #10b981; box-shadow: 0 0 0 4px #d1fae5; }
.timeline-content { display: flex; flex-direction: column; gap: 4px; }
.event-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.event-title b { font-size: 14px; color: #182b45; }
.location-badge { background: #edf5ff; color: #1264d8; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 4px; }
.tag-latest { background: #e6f7ec; color: #1f9d55; font-size: 11px; font-weight: bold; padding: 2px 8px; border-radius: 4px; border: 1px solid #c2ebd0; }
.status-tag { display: inline-block; padding: 2px 8px; font-size: 12px; border-radius: 4px; background: #eee; }
.status-tag.completed { background: #e6f7ec; color: #1f9d55; }
.status-tag.processing { background: #e8f0fe; color: #1a73e8; }
.status-tag.pending { background: #f5f5f5; color: #888; }
.event-time { color: #8291a3; font-size: 12px; font-weight: 500; }
.event-desc { margin: 2px 0 0; color: #4b6078; font-size: 13px; line-height: 1.5; }
.placeholder-icon { font-size: 48px; margin-bottom: 12px; }
@media (max-width: 700px) {
  .milestones-stepper { flex-direction: column; align-items: flex-start; gap: 16px; }
  .stepper-item { flex-direction: row; gap: 12px; text-align: left; }
  .stepper-line { display: none; }
  .milestones-section, .timeline-section { padding: 18px 20px; }
}
</style>

